import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';

import ArticleIcon from '@mui/icons-material/Article';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FinePrintIcon from '@mui/icons-material/ManageSearch';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SubjectIcon from '@mui/icons-material/Subject';
import SyncIcon from '@mui/icons-material/Sync';
import TuneIcon from '@mui/icons-material/Tune';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import AIGeneratorButton from '~/components/AIGenerator/AIGenerateButton';
import {
  LUX_PLUS_FINEPRINT_CONTENT_FLASH,
  LUX_PLUS_FINEPRINT_CONTENT_LPC,
} from '~/components/Common/Forms/OfferForm/constants/fineprint';
import { OfferInclusionsSelect, RichTextEditor } from '~/components/Common/Forms/fields';
import GenerateContentModal from '~/components/Common/GenerateContentModal';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { OFFER_FACING_NAMES } from '~/consts/offerTypes';
import tenants from '~/consts/tenant';

import { useGetOfferContent, useInitiateOfferContentGeneration } from '~/hooks/apis/accommodation/useOfferContent';
import { useSyncPropertyFacilities } from '~/hooks/apis/accommodation/useSyncPropertyFacilities';
import { useGetOfferSchema, useUpdateOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOfferTemplates } from '~/hooks/apis/offers/useGetOfferTemplates';

import InclusionsService from '~/services/InclusionsService';
import { salesforceHost } from '~/services/common';

import buildCustomerPortalPreviewUrl from '~/utils/buildCustomerPortalPreviewUrl';

import OfferGeneratedContentView from '../../../../Edit/OfferGeneratedContentView';
import OfferTemplateModal from '../../../../Edit/OfferTemplateModal';

import type {
  GeneratedContentFields,
  GeneratedContentType,
  OfferSaveAction,
  Props,
  SummaryBodyFormValues,
  TemplateType,
} from './types';
import { getApprovedSaveStatus, normalizeRichTextValue, showSaveError } from './utils';

const AI_BUTTON_SX = {
  m: 0,
  maxWidth: 'none',
  p: 0,
  '&.MuiContainer-root': {
    px: 0,
  },
  '& .MuiGridLegacy-container': {
    m: 0,
    width: 'auto',
  },
  '& .MuiGrid-container': {
    m: 0,
    width: 'auto',
  },
  '& .MuiGridLegacy-container > .MuiGridLegacy-item': {
    p: 0,
  },
  '& .MuiGrid-container > .MuiGrid-item': {
    p: 0,
  },
  '& .MuiGridLegacy-item': {
    p: 0,
  },
  '& .MuiGrid-item': {
    p: 0,
  },
};
const ACCORDION_SX = {
  bgcolor: 'background.paper',
  borderRadius: 1,
  boxShadow: 1,
  overflow: 'hidden',
  '&:before': {
    display: 'none',
  },
};
const ACCORDION_SUMMARY_SX = {
  minHeight: 64,
  px: 3,
  '& .MuiAccordionSummary-content': {
    my: 2,
  },
};
const ACCORDION_DETAILS_SX = {
  bgcolor: 'grey.50',
  borderTop: 1,
  borderColor: 'divider',
  p: 3,
};
const ACTION_BAR_SX = {
  position: 'sticky',
  bottom: 16,
  zIndex: 3,
  mt: 3,
  px: 3,
  py: 1.5,
  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  boxShadow: (theme) => `0 10px 32px ${alpha(theme.palette.common.black, 0.12)}`,
  backdropFilter: 'blur(8px)',
};
const ACTION_BUTTON_SX = {
  minHeight: 36,
  px: 2,
  whiteSpace: 'nowrap',
};
const GENERATED_CONTENT_LABELS: Record<GeneratedContentType, string> = {
  description: 'description',
  nearby: 'locations',
};

type SavedContentSnapshot = SummaryBodyFormValues & {
  name: string;
  offer_tile_inclusion_heading: string;
  preheader: string;
  sale_unit: string;
  slug: string;
  status: string;
  subject_line: string;
  type: string;
};

function useSyncedString(value?: string) {
  const [syncedValue, setSyncedValue] = useState(value ?? '');

  useEffect(() => {
    setSyncedValue(value ?? '');
  }, [value]);

  return [syncedValue, setSyncedValue] as const;
}

export default function DefaultContent({
  offerAdditionalDescription,
  offerDescription,
  offerFacilities,
  offerFinePrint,
  offerGettingThere,
  offerHighlights,
  offerInclusions,
  offerName,
  offerPreheader,
  offerSaleUnit,
  offerSlug,
  offerStatus,
  offerSubjectLine,
  offerTileInclusionHeading,
  offerType,
  packages,
  propertyId,
  vendorId,
}: Props) {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [templateType, setTemplateType] = useState<TemplateType>('location');
  const [isGeneralSettingsExpanded, setIsGeneralSettingsExpanded] = useState(true);
  const [isOfferPageExpanded, setIsOfferPageExpanded] = useState(true);
  const [isMerchandisingEmailExpanded, setIsMerchandisingEmailExpanded] = useState(true);
  const [isInclusionsExpanded, setIsInclusionsExpanded] = useState(true);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isSummaryBodyExpanded, setIsSummaryBodyExpanded] = useState(true);
  const [finePrintMenuAnchorEl, setFinePrintMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isGeneratedContentDialogOpen, setIsGeneratedContentDialogOpen] = useState(false);
  const [isGenerateContentModalVisible, setIsGenerateContentModalVisible] = useState(false);
  const [useGeneratedContentWebSearch, setUseGeneratedContentWebSearch] = useState(false);
  const [generatedContentType, setGeneratedContentType] = useState<GeneratedContentType>('description');
  const [emailPreheaderValue, setEmailPreheaderValue] = useSyncedString(offerPreheader);
  const [emailSubjectLineValue, setEmailSubjectLineValue] = useSyncedString(offerSubjectLine);
  const [headlineValue, setHeadlineValue] = useSyncedString(offerName);
  const [offerTileInclusionHeadingValue, setOfferTileInclusionHeadingValue] =
    useSyncedString(offerTileInclusionHeading);
  const [saleUnitValue, setSaleUnitValue] = useSyncedString(offerSaleUnit);
  const [slugValue, setSlugValue] = useSyncedString(offerSlug);
  const [typeValue, setTypeValue] = useSyncedString(offerType);
  const [statusValue, setStatusValue] = useSyncedString(offerStatus);
  const [pendingSaveAction, setPendingSaveAction] = useState<OfferSaveAction>();
  const { data: offerTemplates = [] } = useGetOfferTemplates();
  const { data: offerSchema } = useGetOfferSchema(offerId);
  const { mutateAsync: updateOffer, isPending: isSavingOffer } = useUpdateOffer(offerId, {
    invalidateOnSuccess: false,
  });
  const offerContentSnapshot = useMemo<SavedContentSnapshot>(
    () => ({
      additional_description: offerAdditionalDescription ?? '',
      description: offerDescription ?? '',
      facilities: offerFacilities ?? '',
      fine_print: offerFinePrint ?? '',
      getting_there: offerGettingThere ?? '',
      highlights: offerHighlights ?? '',
      name: offerName ?? '',
      offer_inclusions: offerInclusions ?? [],
      offer_tile_inclusion_heading: offerTileInclusionHeading ?? '',
      preheader: offerPreheader ?? '',
      sale_unit: offerSaleUnit ?? '',
      slug: offerSlug ?? '',
      status: offerStatus ?? '',
      subject_line: offerSubjectLine ?? '',
      type: offerType ?? '',
    }),
    [
      offerAdditionalDescription,
      offerDescription,
      offerFacilities,
      offerFinePrint,
      offerGettingThere,
      offerHighlights,
      offerInclusions,
      offerName,
      offerPreheader,
      offerSaleUnit,
      offerSlug,
      offerStatus,
      offerSubjectLine,
      offerTileInclusionHeading,
      offerType,
    ],
  );
  const [savedContentSnapshot, setSavedContentSnapshot] = useState(offerContentSnapshot);
  const {
    data: offerContent,
    error: generatedContentError,
    isFetching: isGeneratedContentLoading,
    refetch: refetchGeneratedContent,
  } = useGetOfferContent({
    offerId,
    enabled: isGeneratedContentDialogOpen,
  });
  const { mutateAsync: initiateGeneratedContentGenerationMutation } = useInitiateOfferContentGeneration(offerId);
  const { control, getValues, reset, setValue, watch } = useForm<SummaryBodyFormValues>({
    defaultValues: {
      additional_description: offerAdditionalDescription ?? '',
      description: offerDescription ?? '',
      facilities: offerFacilities ?? '',
      fine_print: offerFinePrint ?? '',
      getting_there: offerGettingThere ?? '',
      highlights: offerHighlights ?? '',
      offer_inclusions: offerInclusions ?? [],
    },
  });
  const additionalDescriptionValue = watch('additional_description') ?? '';
  const descriptionValue = watch('description') ?? '';
  const highlightsValue = watch('highlights') ?? '';
  const locationValue = watch('getting_there') ?? '';
  const facilitiesValue = watch('facilities') ?? '';
  const finePrintValue = watch('fine_print') ?? '';
  const offerInclusionsValue = watch('offer_inclusions');
  const typeOptions = offerSchema?.properties?.type?.enum ?? [];
  const statusOptions = offerSchema?.properties?.status?.enum ?? [];
  const offerPath = slugValue ? `/offer/${slugValue}/${offerId}` : '';
  const structuredFinePrintPath = `/edit-offers/${offerId}/structured-fine-print`;
  const structuredFacilitiesPath = `/edit-offers/${offerId}/structured-facilities`;
  const visibleInclusionCount = offerInclusionsValue.length;
  const previewLinks = useMemo(
    () =>
      offerPath
        ? Object.values(tenants)
            .map((tenant) => {
              const href = buildCustomerPortalPreviewUrl(tenant, offerPath);

              return href
                ? {
                    href,
                    label: tenant.title,
                  }
                : null;
            })
            .filter((link): link is { href: string; label: string } => !!link)
        : [],
    [offerPath],
  );
  const shouldShowStatusFallback = !!statusValue && !statusOptions.some((status) => status === statusValue);
  const generatedContent = useMemo(() => offerContent?.generatedContents ?? [], [offerContent]);
  const visibleGeneratedContent = useMemo(
    () => generatedContent.filter((field) => field.fields === generatedContentType),
    [generatedContent, generatedContentType],
  );
  const hasAnyGeneratedContent = generatedContent.length > 0;
  const hasVisibleGeneratedContent = visibleGeneratedContent.length > 0;
  const generatedContentErrorMessage = generatedContentError instanceof Error ? generatedContentError.message : null;
  const generatedContentLabel = GENERATED_CONTENT_LABELS[generatedContentType];
  const filteredTemplates = useMemo(
    () =>
      offerTemplates.filter((template) => {
        const templateMatchesOfferType = template.offer_type === 'all' || template.offer_type === offerType;

        return template.template_type === templateType && templateMatchesOfferType;
      }),
    [offerTemplates, offerType, templateType],
  );
  const headlineAiContext = useMemo(
    () => ({
      model: 'offer',
      field: 'headline',
      propertyName: headlineValue,
    }),
    [headlineValue],
  );
  const descriptionAiContext = useMemo(
    () => ({
      model: 'offer',
      field: 'description',
      propertyName: headlineValue,
    }),
    [headlineValue],
  );
  const locationAiContext = useMemo(
    () => ({
      model: 'offer',
      field: 'location',
      propertyName: headlineValue,
    }),
    [headlineValue],
  );
  const updateHeadlineAiContext = useCallback(() => headlineAiContext, [headlineAiContext]);
  const updateDescriptionAiContext = useCallback(() => descriptionAiContext, [descriptionAiContext]);
  const updateLocationAiContext = useCallback(() => locationAiContext, [locationAiContext]);
  const hasUnsavedChanges = useMemo(
    () =>
      headlineValue !== savedContentSnapshot.name ||
      saleUnitValue !== savedContentSnapshot.sale_unit ||
      emailSubjectLineValue !== savedContentSnapshot.subject_line ||
      emailPreheaderValue !== savedContentSnapshot.preheader ||
      offerTileInclusionHeadingValue !== savedContentSnapshot.offer_tile_inclusion_heading ||
      slugValue !== savedContentSnapshot.slug ||
      typeValue !== savedContentSnapshot.type ||
      statusValue !== savedContentSnapshot.status ||
      normalizeRichTextValue(additionalDescriptionValue) !==
        normalizeRichTextValue(savedContentSnapshot.additional_description) ||
      normalizeRichTextValue(descriptionValue) !== normalizeRichTextValue(savedContentSnapshot.description) ||
      normalizeRichTextValue(facilitiesValue) !== normalizeRichTextValue(savedContentSnapshot.facilities) ||
      normalizeRichTextValue(finePrintValue) !== normalizeRichTextValue(savedContentSnapshot.fine_print) ||
      normalizeRichTextValue(locationValue) !== normalizeRichTextValue(savedContentSnapshot.getting_there) ||
      normalizeRichTextValue(highlightsValue) !== normalizeRichTextValue(savedContentSnapshot.highlights) ||
      JSON.stringify(offerInclusionsValue) !== JSON.stringify(savedContentSnapshot.offer_inclusions),
    [
      additionalDescriptionValue,
      descriptionValue,
      emailPreheaderValue,
      emailSubjectLineValue,
      facilitiesValue,
      finePrintValue,
      headlineValue,
      highlightsValue,
      locationValue,
      offerInclusionsValue,
      offerTileInclusionHeadingValue,
      saleUnitValue,
      savedContentSnapshot,
      slugValue,
      statusValue,
      typeValue,
    ],
  );
  const handleDiscard = useCallback(() => {
    void showConfirmDialog({
      title: 'Discard changes?',
      description: 'Any unsaved content changes will be lost.',
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }

      setEmailPreheaderValue(savedContentSnapshot.preheader);
      setEmailSubjectLineValue(savedContentSnapshot.subject_line);
      setHeadlineValue(savedContentSnapshot.name);
      setOfferTileInclusionHeadingValue(savedContentSnapshot.offer_tile_inclusion_heading);
      setSaleUnitValue(savedContentSnapshot.sale_unit);
      setSlugValue(savedContentSnapshot.slug);
      setStatusValue(savedContentSnapshot.status);
      setTypeValue(savedContentSnapshot.type);
      reset({
        additional_description: savedContentSnapshot.additional_description,
        description: savedContentSnapshot.description,
        facilities: savedContentSnapshot.facilities,
        fine_print: savedContentSnapshot.fine_print,
        getting_there: savedContentSnapshot.getting_there,
        highlights: savedContentSnapshot.highlights,
        offer_inclusions: savedContentSnapshot.offer_inclusions,
      });

      enqueueSnackbar('Changes discarded', { variant: 'success' });
    });
  }, [
    enqueueSnackbar,
    reset,
    savedContentSnapshot,
    setEmailPreheaderValue,
    setEmailSubjectLineValue,
    setHeadlineValue,
    setOfferTileInclusionHeadingValue,
    setSaleUnitValue,
    setSlugValue,
    setStatusValue,
    setTypeValue,
    showConfirmDialog,
  ]);
  const saveOfferContent = useCallback(
    async (saveAction: OfferSaveAction) => {
      const nextStatus = saveAction === 'draft' ? 'draft' : getApprovedSaveStatus(statusValue);

      setPendingSaveAction(saveAction);

      try {
        const nextContentSnapshot = {
          additional_description: getValues('additional_description'),
          description: getValues('description'),
          facilities: getValues('facilities'),
          fine_print: getValues('fine_print'),
          getting_there: getValues('getting_there'),
          highlights: getValues('highlights'),
          name: headlineValue,
          offer_inclusions: getValues('offer_inclusions'),
          offer_tile_inclusion_heading: offerTileInclusionHeadingValue,
          preheader: emailPreheaderValue,
          sale_unit: saleUnitValue.trim(),
          slug: slugValue,
          status: nextStatus,
          subject_line: emailSubjectLineValue,
          type: typeValue,
        };

        await updateOffer(nextContentSnapshot);

        setSavedContentSnapshot(nextContentSnapshot);
        setStatusValue(nextStatus);
        enqueueSnackbar(saveAction === 'approve' ? 'Offer content saved and approved' : 'Offer content saved', {
          variant: 'success',
        });
      } catch (error) {
        showSaveError(error, enqueueSnackbar);
      } finally {
        setPendingSaveAction(undefined);
      }
    },
    [
      emailPreheaderValue,
      emailSubjectLineValue,
      enqueueSnackbar,
      getValues,
      headlineValue,
      offerTileInclusionHeadingValue,
      saleUnitValue,
      setStatusValue,
      slugValue,
      statusValue,
      typeValue,
      updateOffer,
    ],
  );
  const handleSaveDraft = useCallback(() => {
    void saveOfferContent('draft');
  }, [saveOfferContent]);
  const handleSaveAndApprove = useCallback(() => {
    void saveOfferContent('approve');
  }, [saveOfferContent]);
  const areAllSectionsExpanded =
    isGeneralSettingsExpanded &&
    isOfferPageExpanded &&
    isMerchandisingEmailExpanded &&
    isInclusionsExpanded &&
    isSummaryBodyExpanded;
  const { mutate: syncFacilities, isPending: isSyncingFacilities } = useSyncPropertyFacilities({
    onSuccess: (facilities) => {
      setValue(
        'facilities',
        `${facilities
          .map((item) => `- ${item.name}`)
          .join('\n')}\n\nSurcharges may apply to select facilities and services`,
      );
    },
  });

  useEffect(() => {
    setSavedContentSnapshot(offerContentSnapshot);
    reset({
      facilities: offerContentSnapshot.facilities,
      additional_description: offerContentSnapshot.additional_description,
      description: offerContentSnapshot.description,
      fine_print: offerContentSnapshot.fine_print,
      getting_there: offerContentSnapshot.getting_there,
      highlights: offerContentSnapshot.highlights,
      offer_inclusions: offerContentSnapshot.offer_inclusions,
    });
  }, [offerContentSnapshot, reset]);

  const handleToggleAllSections = () => {
    const nextExpandedState = !areAllSectionsExpanded;

    setIsGeneralSettingsExpanded(nextExpandedState);
    setIsOfferPageExpanded(nextExpandedState);
    setIsMerchandisingEmailExpanded(nextExpandedState);
    setIsInclusionsExpanded(nextExpandedState);
    setIsSummaryBodyExpanded(nextExpandedState);
  };

  const loadLocationTemplate = (template: App.OfferTemplate) => {
    const fieldName = template.template_type === 'fine_print' ? 'fine_print' : 'getting_there';

    setValue(fieldName, template.content);
    setIsTemplateModalVisible(false);
  };

  const appendLuxPlusFinePrint = (content: string) => {
    const currentFinePrintValue = getValues('fine_print');

    setValue('fine_print', `${currentFinePrintValue}\n\n${content}`);
    setFinePrintMenuAnchorEl(null);
  };

  const openFinePrintMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFinePrintMenuAnchorEl(event.currentTarget);
  };

  const showTemplateModal = (type: TemplateType) => {
    setTemplateType(type);
    setIsTemplateModalVisible(true);
  };

  const showGeneratedContentDialog = (contentType: GeneratedContentType) => {
    setGeneratedContentType(contentType);
    setUseGeneratedContentWebSearch(contentType === 'nearby');
    setIsGeneratedContentDialogOpen(true);
  };

  const handleSyncFacilities = () => {
    if (!propertyId) {
      return;
    }

    syncFacilities({ propertyId });
  };

  const importOfferInclusions = async () => {
    if (!packages?.length) {
      enqueueSnackbar('No packages available to import inclusions from.', { variant: 'warning' });
      return;
    }

    const lowestPricePackage = packages.reduce((lowest, next) => (next.cost_price < lowest.cost_price ? next : lowest));

    try {
      const { result } = await InclusionsService.fetchPackageInclusions(offerId);
      const packageInclusions = result[lowestPricePackage.id_salesforce_external] ?? [];

      if (!packageInclusions.length) {
        enqueueSnackbar('No inclusions found on base package, nothing to import', { variant: 'warning' });
        return;
      }

      setValue(
        'offer_inclusions',
        packageInclusions.map((packageInclusion) => ({
          text: packageInclusion.inclusion?.name,
          is_highlighted: packageInclusion.style === 'highlighted',
          lux_plus_tier: packageInclusion.lux_plus_tier ?? null,
          icon: packageInclusion.inclusion?.category?.icon,
          category: packageInclusion.inclusion?.category?.name,
        })),
      );
    } catch (error) {
      enqueueSnackbar(`Failed to import inclusions: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  const applyGeneratedContent = useCallback(
    (content: Partial<App.Offer>) => {
      const generatedFields = content as GeneratedContentFields;

      if (typeof generatedFields.description === 'string') {
        setValue('description', generatedFields.description);
      }

      if (typeof generatedFields.getting_there === 'string') {
        setValue('getting_there', generatedFields.getting_there);
      }

      enqueueSnackbar('Applied selected generated content', { variant: 'success' });
      setIsGeneratedContentDialogOpen(false);
    },
    [enqueueSnackbar, setValue],
  );

  return (
    <Box>
      <OfferTemplateModal
        show={isTemplateModalVisible}
        hide={() => setIsTemplateModalVisible(false)}
        templates={filteredTemplates}
        templateType={templateType}
        loadTemplate={loadLocationTemplate}
      />
      <Dialog open={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Preview offer page</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {previewLinks.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Offer preview links are unavailable until the offer slug and portal config have loaded.
              </Typography>
            )}
            {previewLinks.map((link) => (
              <Button
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                sx={{ justifyContent: 'flex-start' }}
              >
                {link.label}
              </Button>
            ))}
            <Button
              href={`${salesforceHost}/${offerId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{ justifyContent: 'flex-start' }}
            >
              Salesforce
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsPreviewModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isGeneratedContentDialogOpen}
        onClose={() => setIsGeneratedContentDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Generated {generatedContentLabel} content</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Generate, review, and apply suggested {generatedContentLabel} copy from Accommodation Service.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setIsGenerateContentModalVisible(true)}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, whiteSpace: 'nowrap' }}
              >
                {hasVisibleGeneratedContent
                  ? `Regenerate ${generatedContentLabel}`
                  : `Generate ${generatedContentLabel}`}
              </Button>
            </Stack>
            {isGeneratedContentLoading && (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress />
              </Stack>
            )}
            {!isGeneratedContentLoading && generatedContentErrorMessage && (
              <Alert severity="error">Failed to load generated content: {generatedContentErrorMessage}</Alert>
            )}
            {!isGeneratedContentLoading && !generatedContentErrorMessage && !hasVisibleGeneratedContent && (
              <Alert severity="info">No generated content yet.</Alert>
            )}
            {!isGeneratedContentLoading && !generatedContentErrorMessage && hasVisibleGeneratedContent && (
              <OfferGeneratedContentView content={visibleGeneratedContent} applyContent={applyGeneratedContent} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsGeneratedContentDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {isGenerateContentModalVisible && (
        <GenerateContentModal
          onClose={() => setIsGenerateContentModalVisible(false)}
          initiateContentGeneration={initiateGeneratedContentGenerationMutation}
          fetchContent={refetchGeneratedContent}
          includeOfferInGeneration={hasAnyGeneratedContent}
          setUseWebSearch={setUseGeneratedContentWebSearch}
          useWebSearch={useGeneratedContentWebSearch}
          contentType={generatedContentType}
        />
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Default content</Typography>
          <Typography variant="body1" color="text.secondary">
            Master copy used across the offer page, EDM, and merchandising surfaces.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button variant="outlined" size="small" startIcon={<UnfoldMoreIcon />} onClick={handleToggleAllSections}>
            {areAllSectionsExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => setIsPreviewModalOpen(true)}
          >
            Preview offer page
          </Button>
        </Stack>
      </Stack>

      <Accordion
        expanded={isGeneralSettingsExpanded}
        onChange={(_event, isExpanded) => setIsGeneralSettingsExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <TuneIcon color="primary" />
            <Typography variant="h6">General settings</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              maxWidth: 1280,
            }}
          >
            <FormControl fullWidth>
              <FormLabel sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Type</FormLabel>
              <Select
                displayEmpty
                value={typeValue}
                onChange={(event) => setTypeValue(event.target.value)}
                sx={{ bgcolor: 'background.paper' }}
              >
                {!typeValue && (
                  <MenuItem value="" disabled>
                    Loading type
                  </MenuItem>
                )}
                {typeOptions.map((type) => (
                  <MenuItem key={type} value={type}>
                    {(OFFER_FACING_NAMES as Record<string, string>)[type] ?? type}
                  </MenuItem>
                ))}
                {typeValue && !typeOptions.some((type) => type === typeValue) && (
                  <MenuItem value={typeValue}>
                    {(OFFER_FACING_NAMES as Record<string, string>)[typeValue] ?? typeValue}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Status</FormLabel>
              <Select
                displayEmpty
                value={statusValue}
                onChange={(event) => setStatusValue(event.target.value)}
                sx={{ bgcolor: 'background.paper' }}
              >
                {!statusValue && (
                  <MenuItem value="" disabled>
                    Loading status
                  </MenuItem>
                )}
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
                {shouldShowStatusFallback && <MenuItem value={statusValue}>{statusValue}</MenuItem>}
              </Select>
              <FormHelperText>Visible to customers once Rates & Inventory is also approved.</FormHelperText>
            </FormControl>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isOfferPageExpanded}
        onChange={(_event, isExpanded) => setIsOfferPageExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ArticleIcon color="primary" />
            <Typography variant="h6">Offer page</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              required
              label="Headline"
              value={headlineValue}
              onChange={(event) => setHeadlineValue(event.target.value)}
              helperText={`${headlineValue.length} characters · target 80-140`}
            />

            <AIGeneratorButton
              context={headlineAiContext}
              content={headlineValue}
              buttonLabel={headlineValue ? 'Rewrite Headline' : 'Generate Headline'}
              sx={AI_BUTTON_SX}
              updateContext={updateHeadlineAiContext}
              onChoose={setHeadlineValue}
            />

            <TextField
              fullWidth
              label="URL slug"
              value={slugValue}
              onChange={(event) => setSlugValue(event.target.value)}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isMerchandisingEmailExpanded}
        onChange={(_event, isExpanded) => setIsMerchandisingEmailExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <EmailIcon color="primary" />
            <Typography variant="h6">Merchandising & email</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 1fr) minmax(0, 2fr)' },
              }}
            >
              <TextField
                fullWidth
                label="Sale unit"
                value={saleUnitValue}
                helperText="Max 6 chars (room, boat, villa...)"
                slotProps={{ htmlInput: { maxLength: 6 } }}
                onChange={(event) => setSaleUnitValue(event.target.value)}
              />
              <TextField
                fullWidth
                label="Email subject line"
                value={emailSubjectLineValue}
                onChange={(event) => setEmailSubjectLineValue(event.target.value)}
              />
            </Box>

            <TextField
              fullWidth
              label="Email preheader"
              value={emailPreheaderValue}
              onChange={(event) => setEmailPreheaderValue(event.target.value)}
            />

            <Stack spacing={1}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 260 } }}>
                <RichTextEditor control={control} label="Description" name="description" />
              </Box>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => showGeneratedContentDialog('description')}
                sx={{ alignSelf: 'flex-start' }}
              >
                Generate content
              </Button>
              <AIGeneratorButton
                context={descriptionAiContext}
                content={descriptionValue.trim() ? descriptionValue : undefined}
                buttonLabel={descriptionValue.trim() ? 'Rewrite description' : 'Generate description'}
                updateContext={updateDescriptionAiContext}
                onChoose={(value) => setValue('description', value)}
              />
            </Stack>

            <Stack spacing={1}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <RichTextEditor
                  control={control}
                  label="Additional description"
                  name="additional_description"
                  helperText="Optional — appears below the main description on long-form surfaces."
                />
              </Box>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isInclusionsExpanded}
        onChange={(_event, isExpanded) => setIsInclusionsExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ width: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CardGiftcardIcon color="primary" />
              <Typography variant="h6">Offer inclusions (marketing)</Typography>
            </Stack>
            <Chip
              size="small"
              label={`${visibleInclusionCount} ${visibleInclusionCount === 1 ? 'inclusion' : 'inclusions'}`}
            />
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <FormLabel sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Offer inclusion heading</FormLabel>
              <TextField
                fullWidth
                placeholder="Your handpicked inclusions:"
                value={offerTileInclusionHeadingValue}
                onChange={(event) => setOfferTileInclusionHeadingValue(event.target.value)}
              />
            </FormControl>
            <OfferInclusionsSelect
              actions={
                <Button
                  variant="text"
                  startIcon={<MenuBookIcon />}
                  href={vendorId ? `/vendors/${vendorId}/inclusions` : undefined}
                  target="_blank"
                  rel="noreferrer"
                  disabled={!vendorId}
                >
                  Inclusion catalogue
                </Button>
              }
              control={control}
              importOfferInclusions={importOfferInclusions}
              label="Offer inclusions"
              name="offer_inclusions"
              setValue={(name, value) => setValue(name as keyof SummaryBodyFormValues, value as never)}
              variant="offer-builder"
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isSummaryBodyExpanded}
        onChange={(_event, isExpanded) => setIsSummaryBodyExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SubjectIcon color="primary" />
            <Typography variant="h6">Summary & body</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <RichTextEditor control={control} label="Highlights" name="highlights" />

            <Stack spacing={1}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <RichTextEditor control={control} label="Locations" name="getting_there" canUseBrandTokens />
              </Box>
              <Stack
                spacing={1.5}
                alignItems="flex-start"
                sx={{
                  '& > .MuiButton-root': {
                    minHeight: 36,
                    width: { xs: '100%', sm: 'auto' },
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="flex-start"
                  spacing={1.5}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiButton-root': {
                      minHeight: 36,
                      width: { xs: '100%', sm: 'auto' },
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<BookmarksIcon />}
                    onClick={() => showTemplateModal('location')}
                  >
                    Show templates
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => showGeneratedContentDialog('nearby')}
                  >
                    Generate content
                  </Button>
                </Stack>
                <AIGeneratorButton
                  context={locationAiContext}
                  content={locationValue.trim() ? locationValue : undefined}
                  buttonLabel={locationValue.trim() ? 'Rewrite location' : 'Generate location'}
                  sx={AI_BUTTON_SX}
                  updateContext={updateLocationAiContext}
                  onChoose={(value) => setValue('getting_there', value)}
                />
              </Stack>
            </Stack>

            <Stack spacing={1.5}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <RichTextEditor control={control} label="Fine print" name="fine_print" canUseBrandTokens />
              </Box>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{
                  '& .MuiButton-root': {
                    minHeight: 36,
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<BookmarksIcon />}
                  onClick={() => showTemplateModal('fine_print')}
                >
                  Show templates
                </Button>
                <Button variant="outlined" startIcon={<FinePrintIcon />} onClick={openFinePrintMenu}>
                  Append Lux Plus Fine Print
                </Button>
                <Menu
                  anchorEl={finePrintMenuAnchorEl}
                  open={!!finePrintMenuAnchorEl}
                  onClose={() => setFinePrintMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <MenuItem onClick={() => appendLuxPlusFinePrint(LUX_PLUS_FINEPRINT_CONTENT_FLASH)}>
                    Limited Time Lux Exclusive
                  </MenuItem>
                  <MenuItem onClick={() => appendLuxPlusFinePrint(LUX_PLUS_FINEPRINT_CONTENT_LPC)}>
                    Lux Premium Collection
                  </MenuItem>
                </Menu>
                <Button
                  component={Link}
                  to={structuredFinePrintPath}
                  variant="outlined"
                  startIcon={<FinePrintIcon />}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Structured Fine Print
                </Button>
              </Stack>
            </Stack>

            <Stack spacing={1.5}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <RichTextEditor control={control} label="Facilities" name="facilities" />
              </Box>
              <Button
                disabled={!propertyId || isSyncingFacilities}
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={handleSyncFacilities}
                sx={{
                  alignSelf: 'flex-start',
                  minHeight: 36,
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                }}
              >
                {isSyncingFacilities ? 'Syncing...' : 'Sync facilities'}
              </Button>
              <Button
                component={Link}
                to={structuredFacilitiesPath}
                variant="outlined"
                startIcon={<TuneIcon />}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  alignSelf: 'flex-start',
                  minHeight: 36,
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                }}
              >
                Structured Facilities
              </Button>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Box sx={ACTION_BAR_SX}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: hasUnsavedChanges ? 'primary.main' : 'text.disabled',
                flexShrink: 0,
                boxShadow: (theme) =>
                  hasUnsavedChanges ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}` : 'none',
              }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {hasUnsavedChanges ? 'You have unsaved changes' : 'No unsaved changes'}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            justifyContent={{ xs: 'flex-end', sm: 'initial' }}
            spacing={1}
            sx={{
              '& .MuiButton-root': {
                flexShrink: 0,
              },
            }}
          >
            <Button
              variant="text"
              color="inherit"
              disabled={!hasUnsavedChanges || isSavingOffer}
              onClick={handleDiscard}
              sx={ACTION_BUTTON_SX}
            >
              Discard
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={isSavingOffer}
              onClick={handleSaveDraft}
              sx={ACTION_BUTTON_SX}
            >
              {pendingSaveAction === 'draft' ? 'Saving...' : 'Save draft'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isSavingOffer}
              startIcon={<CheckIcon />}
              onClick={handleSaveAndApprove}
              sx={ACTION_BUTTON_SX}
            >
              {pendingSaveAction === 'approve' ? 'Saving...' : 'Save & approve'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
